/**
 * RendererSystem - dono do WebGLRenderer (secao 4.1 do ARCHITECTURE.md).
 *
 * Este modulo NAO cria composer nem passes: quem monta a cadeia de
 * pos-processamento e src/render/PostFX.js. Aqui so existe o contexto WebGL,
 * configurado uma unica vez e do jeito certo para um pipeline HDR:
 *
 *   - antialias:false            -> o AA vem do pos-processamento (SMAA/FXAA)
 *   - stencil:false              -> ninguem usa stencil; economiza banda
 *   - SRGBColorSpace na saida    -> conversao linear->sRGB no ultimo passe
 *   - ACES no tone mapping       -> ver nota "tone mapping" abaixo
 *   - VSMShadowMap               -> ver nota "sombra" abaixo
 *
 * NOTA sobre tone mapping: o three so aplica tone mapping no shader do material
 * quando o alvo de render e a tela (currentRenderTarget === null). Como o jogo
 * desenha o mundo dentro dos alvos HDR do composer, este `toneMapping` fica
 * inerte no caminho normal - quem mapeia de HDR para tela e o ToneMappingEffect
 * do PostFX. Mesmo assim ele e obrigatorio aqui: e o que mantem a imagem correta
 * no caminho de emergencia do Engine (render direto quando nao ha composer, ex.:
 * durante o boot ou se o PostFX falhar).
 *
 * NOTA sobre sombra: por que VSM e nao PCF.
 *
 * O tipo pedido aqui ate a rodada 2 era PCFSoftShadowMap, que o three 0.185 NAO
 * implementa mais: WebGLShadowMap.render() troca por PCFShadowMap e avisa no
 * console ("PCFSoftShadowMap has been deprecated"). O filtro que sobrava esta em
 * shadowmap_pars_fragment.glsl.js, ramo SHADOWMAP_TYPE_PCF: CINCO amostras de
 * disco de Vogel com a fase girada POR PIXEL por interleavedGradientNoise. Com o
 * raio que o Lighting.js calcula no preset alto (penumbra alvo de 11 cm sobre
 * texel de 3.4 cm = 3.24 texels), a penumbra ocupa ~10 px na tela a 10 m e cinco
 * amostras giradas por pixel nao tem como preencher isso: sai chuvisco. Foi
 * exatamente o que a revisao mediu na sombra do poste (14-vp-contraluz_leste), no
 * terminador da laje (05-vp-laje_c2) e na sombra do fio (03-vp-rua_principal_sul,
 * linha pontilhada em vez de continua).
 *
 * Trocar PCFSoft por PCF apenas calaria o aviso - o filtro ja era esse, o ruido
 * continuaria igual. Diminuir o raio nao e opcao deste arquivo (quem calcula
 * radius/normalBias e o Lighting.js, e la o raio ja esta no minimo util).
 *
 * VSM resolve na raiz: o borrao acontece UMA vez, no mapa de momentos (duas
 * passadas separaveis de blurSamples amostras), e nao por fragmento. A penumbra
 * sai continua, sem fase aleatoria, e objeto fino (fio, grade, antena) volta a
 * projetar linha inteira. O Lighting.js JA tem o ramo VSM calibrado (bias,
 * normalBias, radius e blurSamples proprios) e le renderer.shadowMap.type - por
 * isso este arquivo precisa ser criado ANTES do LightingSystem, que e a ordem que
 * o main.js ja usa.
 *
 * O preco, assumido de olho aberto: (1) no VSM o three tambem desenha no mapa de
 * sombra os objetos que so RECEBEM sombra (WebGLShadowMap.renderObject), entao a
 * passada de sombra ganha algumas dezenas de draw calls; (2) as duas passadas de
 * blur custam preenchimento no tamanho do mapa; (3) VSM vaza luz em vao estreito.
 * Sombra suja aparece em todo print; esse preco nao.
 */

import * as THREE from 'three';

/** Cor de fundo antes do ceu existir. Cinza-azulado escuro, nunca preto puro. */
const CLEAR_COLOR = 0x0a0d12;

/** Exposicao base. O PostFX faz a autoexposicao no shader, nao aqui. */
const BASE_EXPOSURE = 1.0;

/**
 * A captura automatizada de tela pede ?capture=1. So nesse caso o back buffer e
 * preservado - no jogo de verdade isso e latencia paga por nada.
 */
function wantsCapture() {
  try {
    return new URLSearchParams(window.location.search).has('capture');
  } catch {
    return false;
  }
}

export class RendererSystem {
  constructor(ctx) {
    this.ctx = ctx;
    /** @type {THREE.WebGLRenderer|null} */
    this.renderer = null;
    this._offSettings = null;
    this._onContextLost = null;
    this._onContextRestored = null;
    this._warned = false;
  }

  /**
   * Cria e configura o renderer. Chamado uma unica vez pelo main.js.
   * @returns {THREE.WebGLRenderer}
   */
  create() {
    const { canvas, settings, engine } = this.ctx;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        // O antialias de hardware (MSAA) e desligado de proposito: o pipeline
        // resolve borda no pos-processamento, onde da para escolher SMAA/FXAA
        // por qualidade. MSAA aqui so gastaria banda sem efeito no composer.
        antialias: false,
        alpha: false,
        depth: true,
        stencil: false,
        premultipliedAlpha: true,
        // preserveDrawingBuffer FICA DESLIGADO no jogo.
        //
        // Ele estava ligado por um motivo de FERRAMENTA, nao de jogo: permitia a
        // revisao visual automatizada chamar canvas.toDataURL() a qualquer
        // instante, e nao so dentro do rAF. O comentario antigo aqui afirmava que
        // "custa pouco em desktop" - afirmacao que nunca tinha sido medida.
        // Quando foi medida, custava: manter o back buffer legivel obriga o
        // navegador a copiar o framebuffer inteiro todo frame e atrapalha a troca
        // de buffers, o que empilha frames na fila de apresentacao. E fila de
        // apresentacao e exatamente o que o jogador sente como "o mouse demora".
        //
        // A ferramenta continua atendida: com ?capture=1 na URL o buffer volta a
        // ser preservado, e so a captura usa esse endereco.
        preserveDrawingBuffer: wantsCapture(),
        // Dica de baixa latencia: autoriza o navegador a nao sincronizar a
        // apresentacao com o compositor, encurtando a fila. Onde nao houver
        // suporte, o atributo e simplesmente ignorado.
        desynchronized: true,
        powerPreference: 'high-performance',
        // Profundidade logaritmica quebra o depth do pos-processamento (SSAO,
        // DOF, motion blur) e nao e necessaria: o mundo tem ~200 m de alcance.
        logarithmicDepthBuffer: false,
        failIfMajorPerformanceCaveat: false
      });
    } catch (err) {
      throw new Error(
        'Nao foi possivel criar o contexto WebGL 2. Atualize o navegador ou os drivers de video. Detalhe: ' +
          (err && err.message ? err.message : String(err))
      );
    }

    // --- espaco de cor e resposta tonal ---------------------------------------
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = BASE_EXPOSURE;

    renderer.setClearColor(CLEAR_COLOR, 1);
    renderer.autoClear = true; // o EffectComposer desliga isso sozinho ao assumir

    // --- sombras --------------------------------------------------------------
    // VSM (ver nota "sombra" no topo do arquivo). Precisa estar definido AQUI,
    // antes do LightingSystem.init(), porque o Lighting le este tipo para
    // escolher o par bias/normalBias e o radius certos.
    renderer.shadowMap.enabled = settings.shadowsEnabled !== false;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    renderer.shadowMap.autoUpdate = true;

    // --- resolucao ------------------------------------------------------------
    // getRenderSize ja combina devicePixelRatio (limitado a 2) com o
    // resolutionScale do preset de qualidade. updateStyle fica em false para
    // nao brigar com o CSS que dimensiona o canvas - mesmo criterio do Engine.
    const { cssWidth, cssHeight, pixelRatio } = engine.getRenderSize();
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(cssWidth, cssHeight, false);

    // --- estatisticas ---------------------------------------------------------
    // Com o composer, um frame faz dezenas de chamadas de render(). Com
    // autoReset ligado, renderer.info seria zerado a cada uma delas e
    // __GAME__.stats() so veria o ultimo passe (1 draw call). Zeramos uma unica
    // vez por frame, no PostFX.update(), que roda logo antes do render.
    renderer.info.autoReset = false;

    this.renderer = renderer;
    this._bindContextEvents(renderer, canvas);
    this._bindSettings(renderer, settings);

    return renderer;
  }

  /**
   * Perda de contexto (troca de GPU, driver reiniciado, aba em segundo plano por
   * muito tempo). Sem o preventDefault o navegador nao tenta restaurar.
   */
  _bindContextEvents(renderer, canvas) {
    this._onContextLost = (event) => {
      event.preventDefault();
      if (!this._warned) {
        this._warned = true;
        console.warn('[Renderer] contexto WebGL perdido; aguardando restauracao');
      }
    };
    this._onContextRestored = () => {
      console.warn('[Renderer] contexto WebGL restaurado');
      // O three recria os recursos sozinho; so as sombras precisam de um empurrao.
      renderer.shadowMap.needsUpdate = true;
    };
    canvas.addEventListener('webglcontextlost', this._onContextLost, false);
    canvas.addEventListener('webglcontextrestored', this._onContextRestored, false);
  }

  /**
   * Reage a mudanca de qualidade. Duas coisas dependem do renderer:
   * a escala de resolucao (pixel ratio) e o estado do shadow map.
   */
  _bindSettings(renderer, settings) {
    this._offSettings = settings.onChange((key) => {
      if (key !== 'quality' && key !== 'shadowsEnabled' && key !== 'resolutionScale') return;

      // resolutionScale entra no pixelRatio; engine.resize() reaplica tudo
      // (renderer, cameras, composer e os onResize dos sistemas) de uma vez.
      const engine = this.ctx.engine;
      if (engine && typeof engine.resize === 'function') engine.resize();

      const shadowsOn = settings.shadowsEnabled !== false;
      if (renderer.shadowMap.enabled !== shadowsOn) {
        renderer.shadowMap.enabled = shadowsOn;
        renderer.shadowMap.needsUpdate = true;
        // Ligar/desligar sombra muda o programa dos materiais; sem marcar
        // needsUpdate o three continua usando o shader antigo e a cena fica com
        // sombra congelada. Isso e uma travessia unica, so na troca de preset.
        this._invalidateMaterials();
      }
    });
  }

  /** Marca todos os materiais da cena para recompilar. Nunca chamado por frame. */
  _invalidateMaterials() {
    const scene = this.ctx.scene;
    if (!scene) return;
    try {
      scene.traverse((obj) => {
        const material = obj.material;
        if (!material) return;
        if (Array.isArray(material)) {
          for (let i = 0; i < material.length; i++) material[i].needsUpdate = true;
        } else {
          material.needsUpdate = true;
        }
      });
    } catch (err) {
      console.warn('[Renderer] falha ao invalidar materiais:', err);
    }
  }

  dispose() {
    if (this._offSettings) {
      this._offSettings();
      this._offSettings = null;
    }
    const canvas = this.ctx.canvas;
    if (canvas) {
      if (this._onContextLost) canvas.removeEventListener('webglcontextlost', this._onContextLost);
      if (this._onContextRestored) canvas.removeEventListener('webglcontextrestored', this._onContextRestored);
    }
    this._onContextLost = null;
    this._onContextRestored = null;
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }
  }
}
